import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import api, { asArray } from '../services/api';
import { PageHeader, PillTabs, DarkPanel, dark, SortTh, sortRows } from '../components/ui';

const COUNT = 10;

function fmtPrice(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(2);
  return n.toFixed(4);
}

/** 84944757 -> "84.9M" (keeps the column narrow on phones). */
function fmtVol(v) {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  if (!n) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(n);
}

/** "+1.23%", "-0.5%", "(2.1%)", 1.23 -> number (NaN when unparseable). */
function pctNum(v) {
  if (typeof v === 'number') return v;
  const n = parseFloat(String(v ?? '').replace(/[%+,()\s]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

/** Up = green, down = red, flat/unknown = slate — one palette for every tab. */
const TONES = {
  up: {
    text: 'text-emerald-300',
    pill: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30',
    bar: 'border-l-emerald-400',
    arrow: '▲',
  },
  down: {
    text: 'text-rose-300',
    pill: 'bg-rose-500/15 text-rose-300 ring-rose-400/30',
    bar: 'border-l-rose-400',
    arrow: '▼',
  },
  flat: {
    text: 'text-slate-200',
    pill: 'bg-white/5 text-slate-300 ring-white/10',
    bar: 'border-l-slate-600',
    arrow: '•',
  },
};
const toneOf = (pct) => (!Number.isFinite(pct) || pct === 0 ? TONES.flat : pct > 0 ? TONES.up : TONES.down);

function ChangePill({ pct }) {
  const t = toneOf(pct);
  return (
    <span className={`inline-flex items-center gap-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-bold tabular-nums ring-1 ring-inset sm:px-2 sm:text-xs ${t.pill}`}>
      <span className="text-[9px] sm:text-[10px]">{t.arrow}</span>
      {Number.isFinite(pct) ? `${Math.abs(pct).toFixed(2)}%` : '—'}
    </span>
  );
}

function TickerLink({ sym, label }) {
  return (
    <Link to={`/stock/${encodeURIComponent(sym)}`} className="block truncate font-bold text-white hover:text-emerald-300">
      {label || sym}
    </Link>
  );
}

/** Fixed column widths (table-fixed) so Symbol / Price / Change line up identically
 *  on every tab and the Change pill never gets squeezed off a phone. */
function Cols({ widths }) {
  return (
    <colgroup>
      {widths.map((w, i) => <col key={i} style={{ width: w }} />)}
    </colgroup>
  );
}

/** In-panel ▲ Up / ▼ Down switch — the selected side takes its own color. */
function PanelSwitch({ options, value, onChange }) {
  const on = { up: 'bg-emerald-500 text-white shadow-sm', down: 'bg-rose-500 text-white shadow-sm' };
  return (
    <div className="flex shrink-0 gap-1 rounded-lg bg-white/5 p-0.5 ring-1 ring-white/10">
      {options.map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-semibold transition ${
            value === k ? (on[k] || 'bg-white text-slate-900') : 'text-slate-400 hover:text-white'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * One table for every Market tab. Rows are normalized first ({ key, sym, label, price,
 * pct, vol } — see the to*Rows helpers) so every tab looks the same: symbol, price, a
 * change pill and a thin green/red bar on the left — one line per row. Tabs whose data
 * has volume (AI Move, Options, Pre/Post) get a 4th Vol column; the quote boards
 * (indexes, ETFs, commodities, crypto) have no volume and stay at 3 columns.
 */
function MoveTable({ rows, emptyText = 'No data', priceLabel = 'Price', volLabel = 'Vol', showVol = false, wideSymbol = false }) {
  const [sort, setSort] = useState(null);
  const list = sortRows(rows || [], sort, (r, k) => (k === 'price' ? r.price : k === 'volume' ? r.vol : r.pct));
  const widths = showVol
    ? (wideSymbol ? ['38%', '19%', '25%', '18%'] : ['30%', '23%', '27%', '20%'])
    : ['44%', '27%', '29%'];
  const cols = widths.length;
  return (
    <div className={dark.wrap}>
      <table className={`${dark.table} table-fixed`}>
        <Cols widths={widths} />
        <thead className={dark.thead}>
          <tr>
            <th className={`${dark.th} text-left`}>Symbol</th>
            <SortTh label={priceLabel} sortKey="price" sort={sort} onSort={setSort} />
            <SortTh label="Change" sortKey="change" sort={sort} onSort={setSort} />
            {showVol && <SortTh label={volLabel} sortKey="volume" sort={sort} onSort={setSort} />}
          </tr>
        </thead>
        <tbody>
          {list.length === 0 ? (
            <tr><td colSpan={cols} className={dark.empty}>{emptyText}</td></tr>
          ) : list.map((r) => {
            const t = toneOf(r.pct);
            return (
              <tr key={r.key} className={dark.tr}>
                <td className={`${dark.td} border-l-2 ${t.bar}`}>
                  {r.sym ? <TickerLink sym={r.sym} label={r.label} /> : <span className="font-bold text-white">{r.label || '—'}</span>}
                </td>
                <td className={`${dark.td} text-right font-semibold tabular-nums ${t.text}`}>${fmtPrice(r.price)}</td>
                <td className={`${dark.td} text-right`}><ChangePill pct={r.pct} /></td>
                {showVol && <td className={`${dark.td} text-right tabular-nums text-slate-400`}>{r.volText ?? fmtVol(r.vol)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Pre / Post movers. */
const toMoverRows = (rows) => (rows || []).slice(0, COUNT).map((r, i) => ({
  key: `${r.ticker}-${i}`,
  sym: r.ticker,
  price: r.price,
  pct: pctNum(r.changePercentage),
  vol: r.volume,
}));

/** Options — contract label (e.g. "NVDA 10/17 $180C", C/P in the label) links to the underlying. */
const toOptionRows = (rows) => (rows || []).map((r) => ({
  key: r.contract,
  sym: r.ticker,
  label: r.label,
  price: r.price,
  pct: pctNum(r.changePercentage),
  vol: r.volume,
}));

/** AI Move (momentum up/down). */
const toMomentumRows = (rows) => (rows || []).map((r, i) => ({
  key: `${r.ticker}-${i}`,
  sym: r.ticker,
  price: r.price,
  pct: pctNum(r.changePercentage),
  vol: r.volume,
}));

/** Rapid Move — change = the quick move (not the day's change), Vol = relative volume,
 *  🔔 = this one was also sent as an alert. */
const toRapidRows = (rows) => (rows || []).map((r) => ({
  key: `${r.ticker}-${r.detectedAt}`,
  sym: r.ticker,
  label: `${r.alerted ? '🔔 ' : ''}${r.ticker} · ${r.detectedAt}`,
  price: r.price,
  pct: Number(r.movePct),
  vol: Number(r.relVolume),
  volText: `${Number(r.relVolume).toFixed(1)}x`,
}));

/** Crypto / Indexes / Commodities / ETFs (no volume in this data). */
const toBoardRows = (rows) => (rows || []).map((r) => ({
  key: r.symbol,
  sym: r.symbol,
  label: r.label,
  price: r.price,
  pct: pctNum(r.changePercent),
}));

/** Top 10 winners (dir 'up', biggest % gain first) or losers ('down', biggest %
 *  drop first) of a quote-board group — rows that moved the other way are left out. */
function topBoard(rows, dir) {
  const pct = (r) => Number(r.changePercent);
  return (rows || [])
    .filter((r) => Number.isFinite(pct(r)) && (dir === 'up' ? pct(r) > 0 : pct(r) < 0))
    .sort((a, b) => (dir === 'up' ? pct(b) - pct(a) : pct(a) - pct(b)))
    .slice(0, COUNT);
}

// Every panel is a single top-10 list with a ▲ Up / ▼ Down switch in its header,
// so nothing needs scrolling past one list to reach another. (Gainers/Losers was
// dropped — it duplicated AI Move.)
const TABS = [
  { key: 'ai', label: 'AI Move' },
  // Rapid Move moved to Admin → Manage Signals → Live watch → Rapid Moves.
  { key: 'options', label: 'Options' },
  { key: 'indexes', label: 'Indexes' },
  { key: 'etfs', label: 'ETFs' },
  { key: 'commodities', label: 'Commodities' },
  { key: 'crypto', label: 'Crypto' },
  { key: 'pre', label: 'Pre Market' },
  { key: 'post', label: 'Post Market' },
];

const BOARD_GROUP = { crypto: 'cryptos', indexes: 'indexes', commodities: 'commodities', etfs: 'etfs' };

/** Loads a Market endpoint the first time its tab is opened. */
function useLazyLoad(open, url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || data || loading) return;
    setLoading(true);
    api.get(url)
      .then((r) => setData(r.data || {}))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, [open, data, loading, url]);
  return [data, loading];
}

/** Market page for users (/market) and admins (/admin/market) — same page, same name. */
export default function Market({ title = 'Market' }) {
  const [board, setBoard] = useState(null);
  const [up, setUp] = useState([]);
  const [down, setDown] = useState([]);
  const [active, setActive] = useState('ai');
  const [dir, setDir] = useState('up');                               // ▲ Up | ▼ Down — shared by every tab
  const isExt = active === 'pre' || active === 'post';
  const [ext, extLoading] = useLazyLoad(isExt, '/stock/extended-hours');
  const [opts, optsLoading] = useLazyLoad(active === 'options', '/stock/options-movers');
  const [rapid, setRapid] = useState(null);

  // Rapid Move is re-scanned every minute on the server — keep the open tab current
  useEffect(() => {
    if (active !== 'rapid') return undefined;
    let cancelled = false;
    const load = () => api.get('/stock/rapid-moves')
      .then((r) => { if (!cancelled) setRapid(r.data || {}); })
      .catch(() => { if (!cancelled) setRapid((d) => d || {}); });
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [active]);

  useEffect(() => {
    let cancelled = false;
    api.get('/stock/surge?limit=10').then((r) => { if (!cancelled) setUp(asArray(r.data)); }).catch(() => {});
    api.get('/stock/surge-down?limit=10').then((r) => { if (!cancelled) setDown(asArray(r.data)); }).catch(() => {});

    const loadBoard = () => {
      api.get('/stock/market-board')
        .then((r) => { if (!cancelled && r.data) setBoard(r.data); })
        .catch(() => {});
    };
    loadBoard();
    const t = setTimeout(loadBoard, 2500); // retry once if the first response was empty
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  const isUp = dir === 'up';
  const noneText = isUp ? 'No winners right now' : 'No losers right now';
  let body;
  switch (active) {
    case 'ai':
      body = <MoveTable rows={toMomentumRows(isUp ? up : down)} emptyText="Loading AI movement…" showVol />;
      break;
    case 'rapid':
      body = !rapid
        ? <p className={dark.empty}>Loading rapid moves…</p>
        : <MoveTable rows={toRapidRows(isUp ? rapid.up : rapid.down)} emptyText={rapid.message || (isUp ? 'No rapid moves up today' : 'No rapid moves down today')} volLabel="RVol" showVol wideSymbol />;
      break;
    case 'options':
      body = optsLoading && !opts
        ? <p className={dark.empty}>Scanning option chains…</p>
        : <MoveTable rows={toOptionRows(isUp ? opts?.gainers : opts?.losers)} emptyText={opts?.message || noneText} priceLabel="Prem" showVol wideSymbol />;
      break;
    case 'pre':
    case 'post':
      body = extLoading && !ext
        ? <p className={dark.empty}>Loading {active === 'post' ? 'post' : 'pre'} market…</p>
        : <MoveTable rows={toMoverRows(ext?.[`${active}${isUp ? 'Gainers' : 'Losers'}`])} emptyText={noneText} showVol />;
      break;
    default:
      body = <MoveTable rows={toBoardRows(topBoard(board?.[BOARD_GROUP[active]], dir))} emptyText={board ? noneText : 'Loading quotes…'} />;
  }

  // Just "Winners" / "Losers" — the selected tab pill already says which section. The
  // only subtitle is a delay note, and only where the data really is delayed (options).
  const panelTitle = isUp ? 'Winners' : 'Losers';
  const subtitle = active === 'options' ? '15 min delayed'
    : active === 'rapid' && rapid?.windowMinutes
      ? `${rapid.minMovePct}%+ in ${rapid.windowMinutes} min · ${rapid.minRelVolume}x+ volume · $${rapid.minMarketCapBillion}B+ · ${rapid.alertsToday ?? 0}/${rapid.maxAlertsPerDay} alerts`
      : undefined;

  const panelSwitch = <PanelSwitch options={[['up', '▲ Up'], ['down', '▼ Down']]} value={dir} onChange={setDir} />;

  return (
    <div className="pb-8">
      <PageHeader title={title} />
      {/* One swipeable pill row on phones (was a 4-row button grid that took most of
          the first screen); wraps on wider screens. */}
      <PillTabs tabs={TABS} active={active} onChange={setActive} className="mb-4" />
      <DarkPanel title={panelTitle} subtitle={subtitle} right={panelSwitch} bodyClassName="p-0">
        {body}
      </DarkPanel>
    </div>
  );
}

